import * as Sentry from '@sentry/capacitor';
import { init as sentryReactInit, type ErrorEvent } from '@sentry/react';
import { Capacitor } from '@capacitor/core';

/**
 * Initialize Sentry crash + error reporting. Called once at app boot
 * from `main.tsx` — must run **before** React renders so the
 * ErrorBoundary's componentDidCatch hands events to a wired client.
 *
 * `@sentry/capacitor` wraps the native iOS / Android Sentry SDKs and
 * automatically captures hard crashes from the WebView host (Swift /
 * Java / Kotlin). On the web build it falls through to the standard
 * browser SDK. Two transports, one init call.
 *
 * Behavior:
 *  - **No DSN**: no-op. Local dev and CI without secrets just skip.
 *  - **localhost**: also no-op — events from `vite` dev runs would
 *    pollute the prod Sentry project's issue list.
 *  - **Production / TestFlight / staging**: initializes with PII
 *    scrubbing on, traces sampled at 10%, full session replay off
 *    (the field app handles customer data — replay would record
 *    addresses and is overkill for our needs anyway).
 *
 * Audience-aware: the same bundle ships as both the field native app
 * and the customer web portal (and eventually the customer iOS app).
 * Tagging by audience + platform makes the Sentry dashboard usable —
 * "show me only crashes from the field iOS app" is one click instead
 * of a query.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.PROD) {
      console.warn(
        'VITE_SENTRY_DSN is not set; Sentry crash reporting is disabled for this build.',
      );
    }
    return;
  }

  // Skip localhost / file:// / capacitor://localhost-during-dev URLs.
  // Native builds don't have `window.location.hostname` semantically
  // matching localhost (Capacitor uses capacitor://localhost) but they
  // DO have PROD=true via Vite's build mode — we trust that signal.
  const isLocalDev =
    !import.meta.env.PROD &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');
  if (isLocalDev) return;

  const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'
  const isNative = Capacitor.isNativePlatform();

  // `@sentry/capacitor` expects the React `init` to be passed in as a
  // sibling argument when running native — that lets the native SDK
  // hand off browser-side events into the same project. On web we just
  // call the React init directly with the same config.
  const sharedConfig = {
    dsn,
    // Release format `dispatch-field@1.0.0+gitsha`. Wired up at build
    // time via vite.config.ts; falls back to the package version when
    // CI hasn't injected a SHA.
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    environment: (import.meta.env.MODE as string) || 'production',

    // PII scrubbing. ISPs hold sensitive customer data — name, email,
    // address, billing details. Defaults to false so neither breadcrumb
    // bodies nor IP addresses leave the device unscrubbed. Specific
    // user attribution is added via Sentry.setUser() after login, with
    // only the user GUID, never email/name.
    sendDefaultPii: false,

    // 10% of transactions sampled. Bumps the Sentry quota usage on the
    // free tier — adjust if we go premium. Set to 0 to disable
    // performance tracing entirely.
    tracesSampleRate: 0.1,

    // beforeSend lets us strip out anything we know is sensitive that
    // the SDK might still capture. The Mapbox token is the most
    // commonly leaked piece — it sometimes appears in fetch URLs.
    beforeSend(event: ErrorEvent) {
      // Once main.tsx has decided to reload over a failed chunk fetch,
      // Vite resolves the pending import() with undefined and every
      // lazy() wrapper throws "Cannot read properties of undefined
      // (reading 'X')" in the moment before the reload lands. Nobody
      // sees it, so don't report it.
      if (reloadingForStaleChunk) return null;
      if (event.request?.url) {
        event.request.url = event.request.url
          .replace(/access_token=[^&]+/g, 'access_token=REDACTED');
      }
      return event;
    },

    initialScope: {
      tags: {
        platform,
        // 'native' vs 'web' is more useful than the platform string on
        // its own — answers "did this crash happen in the bundled app
        // or in the browser tab?"
        runtime: isNative ? 'native' : 'web',
        // App audience. Hardcoded "field" for the dispatch app build;
        // the customer-portal app will set its own value when it ships.
        audience: 'field',
      },
    },
  };

  if (isNative) {
    Sentry.init(sharedConfig, sentryReactInit);
  } else {
    sentryReactInit(sharedConfig);
  }
}

let reloadingForStaleChunk = false;

/**
 * Called by the vite:preloadError handler in main.tsx the moment it
 * commits to reloading the page. In-memory on purpose: the flag dies
 * with the reload, so only the doomed page's errors are suppressed.
 */
export function markReloadingForStaleChunk(): void {
  reloadingForStaleChunk = true;
}

/**
 * Set the user context after login so events get attributed to a known
 * tenant + user GUID. Email / name deliberately left out — Sentry only
 * gets the IDs needed to correlate to our DB.
 */
export function setSentryUser(userId: string, tenantId?: string): void {
  Sentry.setUser({ id: userId });
  if (tenantId) Sentry.setTag('tenantId', tenantId);
}

/** Clear the user context on logout. */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}

/**
 * Manually report a caught exception. Use this when you want a
 * controlled error report (e.g. from an ErrorBoundary or a catch
 * block where we don't want to re-throw).
 */
export function reportException(err: unknown, context?: Record<string, unknown>): void {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      Sentry.captureException(err);
    });
  } else {
    Sentry.captureException(err);
  }
}
